import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { KeyRound, Pencil } from 'lucide-react';

import { useTenantMe } from '@/components/layout/TopBar';
import { PasswordChangeForm } from '@/components/profile/PasswordChangeForm';
import { LOCALE_LABELS, ProfileEditForm } from '@/components/profile/ProfileEditForm';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ROLE_COLORS, ROLE_LABELS, getInitials, maskId } from '@/lib/role-display';
import { useAuth } from '@/store/auth';

export const Route = createFileRoute('/_authed/profile')({ component: ProfilePage });

export function ProfilePage() {
  const user = useAuth((s) => s.session?.user);
  const { data: tenant } = useTenantMe();
  const [editing, setEditing] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  if (!user) return null;

  return (
    <div className="p-6 max-w-2xl space-y-6">
      <h1 className="text-2xl font-bold">Mon profil</h1>
      <Card>
        <CardHeader className="flex flex-row items-center gap-4 space-y-0">
          <Avatar fallback={getInitials(user.firstName, user.lastName)} className="h-16 w-16 text-xl" />
          <div className="min-w-0 flex-1">
            <CardTitle className="truncate">{user.firstName} {user.lastName}</CardTitle>
            <div className="text-sm text-muted-foreground truncate">{user.email}</div>
            <Badge className={`mt-2 ${ROLE_COLORS[user.role]}`}>{user.customRoleName ?? ROLE_LABELS[user.role]}</Badge>
            {user.customRoleName && (
              <div className="mt-1 text-xs text-muted-foreground">Basé sur {ROLE_LABELS[user.role]}</div>
            )}
          </div>
          {!editing && (
            <Button variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4 mr-2" /> Modifier
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {editing ? (
            <ProfileEditForm onDone={() => setEditing(false)} />
          ) : (
            <dl className="grid grid-cols-[10rem_1fr] gap-y-2 text-sm">
              <dt className="text-muted-foreground">Organisation</dt>
              <dd>{tenant?.name ?? '—'}</dd>
              <dt className="text-muted-foreground">Identifiant</dt>
              <dd className="font-mono">{maskId(user.tenantId)}</dd>
              <dt className="text-muted-foreground">Langue préférée</dt>
              <dd>{user.preferredLocale ? LOCALE_LABELS[user.preferredLocale] ?? user.preferredLocale : '—'}</dd>
            </dl>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Mot de passe</CardTitle>
          {!changingPassword && (
            <Button variant="outline" onClick={() => setChangingPassword(true)}>
              <KeyRound className="h-4 w-4 mr-2" /> Modifier le mot de passe
            </Button>
          )}
        </CardHeader>
        {changingPassword && (
          <CardContent>
            <PasswordChangeForm onDone={() => setChangingPassword(false)} />
          </CardContent>
        )}
      </Card>
    </div>
  );
}
